import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getClipFilterComments } from '../../api/clipFiltersApi';
import { isDeletedComment } from '../../helpers/comments';
import { useAppSelector } from '../../store/hooks';
import { useClipFavourites } from '../ClipFavourites/context';
import { collectFilterValues, emptyFilter, evaluateFilter, FilterComment, FilterConfig, FilterToken, tokenKey, tokenNeedsComments } from './types';

export type FilterSidebarView = 'comments' | 'selected' | 'favourites' | 'filter';
type FilterContext = ReturnType<typeof useFilterState>;
const Context = createContext<FilterContext | null>(null);

const useFilterState = () => {
  const { feedId, editor } = useClipFavourites();
  const [config, setConfig] = useState<FilterConfig>(emptyFilter);
  const [searchFields, setSearchFields] = useState({ row: true, qualifier: true, category: true, notes: true, comments: true });
  const [sidebarView, setSidebarView] = useState<FilterSidebarView>('comments');
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [commentRevision, setCommentRevision] = useState(0);
  const commentRefresh = useAppSelector((state) => state.composersStore.commentFetchTrigger);
  const [commentState, setCommentState] = useState<{ key: string; status: 'loading' | 'ready' | 'error'; data: FilterComment[] }>({ key: '', status: 'loading', data: [] });
  const needsComments = config.tokens.some(tokenNeedsComments);
  const loadComments = needsComments || (sidebarVisible && sidebarView === 'filter');
  const clipIds = useMemo(() => editor.rows.flatMap((row) => row.clips.map((clip) => String(clip.id))).sort().join(','), [editor.rows]);
  const commentKey = JSON.stringify([feedId, clipIds, commentRefresh, commentRevision]);
  const commentsReady = commentState.key === commentKey && commentState.status === 'ready';
  const commentsError = commentState.key === commentKey && commentState.status === 'error';
  const commentsLoading = loadComments && !commentsReady && !commentsError;
  const refreshComments = useCallback(() => setCommentRevision((value) => value + 1), []);

  useEffect(() => {
    if (!feedId || !loadComments) return;
    let cancelled = false;
    setCommentState({ key: commentKey, status: 'loading', data: [] });
    getClipFilterComments(feedId).then(({ data }) => {
      if (!cancelled) setCommentState({ key: commentKey, status: 'ready', data: data.filter((comment) => !isDeletedComment(comment)) });
    }).catch(() => {
      if (!cancelled) setCommentState({ key: commentKey, status: 'error', data: [] });
    });
    return () => { cancelled = true; };
  }, [feedId, loadComments, commentKey]);

  const addToken = useCallback((token: FilterToken) => {
    const next = { ...token, value: token.value.trim() };
    if (!next.value) return;
    setConfig((previous) => {
      if (previous.tokens.some((item) => tokenKey(item) === tokenKey(next))) return previous;
      const tokens = next.field === 'commenter'
        ? previous.tokens.filter((item) => item.field !== 'commenter' || (next.value !== '*' && item.value !== '*'))
        : previous.tokens;
      return { ...previous, tokens: [...tokens, next] };
    });
  }, []);
  const removeToken = useCallback((token: FilterToken) => setConfig((previous) => ({
    ...previous, tokens: previous.tokens.filter((item) => tokenKey(item) !== tokenKey(token)),
  })), []);
  const clear = useCallback(() => setConfig(emptyFilter()), []);
  const openFilter = useCallback(() => {
    setSidebarVisible(true);
    setSidebarView('filter');
  }, []);
  const values = useMemo(() => collectFilterValues(editor.rows), [editor.rows]);
  const result = useMemo(() => evaluateFilter(editor.rows, config, commentState.data, commentsReady), [editor.rows, config, commentState.data, commentsReady]);
  const showMatchAllZero = useMemo(() => config.tokens.length > 0 && config.match === 'all' && result.shown === 0 &&
    (!needsComments || commentsReady) &&
    evaluateFilter(editor.rows, { ...config, match: 'any' }, commentState.data, commentsReady).shown > 1,
  [config, result.shown, needsComments, commentsReady, editor.rows, commentState.data]);
  const commenters = useMemo(() => {
    const byId = new Map<string, { id: string; username: string; avatar_url: string | null; count: number }>();
    if (commentsReady) commentState.data.forEach((comment) => {
      if (!comment.author) return;
      const id = String(comment.author.id);
      const previous = byId.get(id);
      if (previous) previous.count += 1;
      else byId.set(id, { ...comment.author, username: comment.author.username || 'Unknown user', id, count: 1 });
    });
    return Array.from(byId.values()).sort((a, b) => a.username.localeCompare(b.username));
  }, [commentState.data, commentsReady]);

  return {
    feedId, config, setConfig, addToken, removeToken, clear, values, searchFields, setSearchFields,
    sidebarView, setSidebarView, sidebarVisible, setSidebarVisible, openFilter,
    commentsLoading, commentsError, commentsReady, commenters,
    refreshComments, reloadComments: refreshComments,
    waitingForComments: needsComments && !commentsReady,
    showMatchAllZero,
    commentCount: commentsReady ? commentState.data.length : 0,
    active: config.tokens.length > 0, ...result,
  };
};

export const ClipFilterProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useFilterState();
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useClipFilter = () => {
  const context = useContext(Context);
  if (!context) throw new Error('Clip filters require a video page');
  return context;
};
